import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '@/components/SEO';
import { CoverImage } from '@/components/CoverImage';
import { postContent } from './blogContent';
import { readingTime } from '@/lib/readingTime';
import { ArrowRight, Clock, Search, X } from 'lucide-react';
import '@/styles/forms.css';
import '@/styles/tabs.css';
import '@/styles/landing.css';
import PillTabs from '@/components/ds/PillTabs';

export const blogPosts = [
  {
    slug: 'heatmaps-vs-session-recordings',
    title: 'Heatmaps vs Session Recordings: Which to Use (2026)',
    metaDescription: 'Heatmaps show where users click and scroll. Session recordings show you why. When to use each, where both fall short, and how they fit a UX review.',
    excerpt: 'Heatmaps tell you where users click and scroll; session recordings tell you why. When to reach for each, and where both leave gaps.',
    category: 'Comparisons',
    date: '2026-11-02',
    keyword: 'heatmaps vs session recordings',
    cover: '/blog/heatmaps-vs-session-recordings.png',
  },
  {
    slug: 'free-trial-conversion-rate',
    title: 'Free Trial Conversion Rate: How to Improve It (2026)',
    metaDescription: 'Signups climb but trials do not convert? Lift your free trial conversion rate by fixing the first-session friction that quietly loses new users.',
    excerpt: 'Most trials are lost in the first session, not at the pricing page. How to lift free trial conversion by fixing the friction a new user hits early.',
    category: 'Growth',
    date: '2026-10-26',
    keyword: 'free trial conversion rate',
    cover: '/blog/free-trial-conversion-rate.png',
  },
  {
    slug: 'figma-plugins-for-ux-review',
    title: '6 Figma Plugins for Faster UX Reviews (2026)',
    metaDescription: 'Six Figma plugins that speed up a UX review: accessibility and contrast checks, consistency linting, live-site import, and bulk layer audits.',
    excerpt: 'The plugins that turn a slow, manual UX review into a fast pass, from contrast checks to consistency linting. What each one is good at and where it stops.',
    category: 'Tools & Reviews',
    date: '2026-10-12',
    keyword: 'figma plugins for ux review',
    cover: '/blog/figma-plugins-for-ux-review.png',
  },
  {
    slug: 'saas-dashboard-ux-audit',
    title: 'SaaS Dashboard UX Audit: A Practical Guide (2026)',
    metaDescription: 'A step-by-step dashboard UX audit for SaaS teams: what to check on the empty state, data density, navigation, and the moments where users quietly stall.',
    excerpt: 'What to check on a SaaS dashboard: the empty state, data density, navigation, and the moments where users quietly stall.',
    category: 'Guides',
    date: '2026-09-28',
    dateModified: '2026-09-28',
    keyword: 'dashboard ux audit',
    cover: '/blog/saas-dashboard-ux-audit.png',
  },
  {
    slug: 'ecommerce-checkout-ux-audit',
    title: 'Ecommerce and Checkout UX Audit',
    metaDescription: 'A checklist-driven ecommerce UX audit of product pages, cart, guest checkout, form friction, payment trust, and mobile checkout, plus the fixes.',
    excerpt: 'Product pages, cart, guest checkout and payment trust, checked step by step, with the fixes.',
    category: 'Guides',
    date: '2026-09-21',
    dateModified: '2026-09-21',
    keyword: 'ecommerce ux audit',
    cover: '/blog/ecommerce-checkout-ux-audit.png',
  },
  {
    slug: 'automated-ux-audit-vs-agency',
    title: 'Automated UX Audit vs Hiring an Agency',
    metaDescription: 'Automated UX audit or a UX agency? An honest comparison of cost, depth, and speed, and how to tell which UX audit services fit your team and budget.',
    excerpt: 'Software or an agency? An honest comparison of cost, depth and speed, and how to tell which one your team needs.',
    category: 'Comparisons',
    date: '2026-09-07',
    dateModified: '2026-09-07',
    keyword: 'ux audit services',
    cover: '/blog/automated-ux-audit-vs-agency.png',
  },
  {
    slug: 'ux-audit-for-startups',
    title: 'UX Audit for Startups: The 2-Hour Version',
    metaDescription: 'A time-boxed UX audit a founder can run alone in two hours before a launch or a raise. Scope it, score fast, and leave with a ranked fix list.',
    excerpt: 'A two-hour review a founder can run alone before a launch or a raise, ending with a ranked fix list.',
    category: 'Guides',
    date: '2026-08-24',
    dateModified: '2026-08-24',
    keyword: 'audit ux startup',
    cover: '/blog/ux-audit-for-startups.png',
  },
  {
    slug: 'ai-ux-audit-what-ai-catches',
    title: 'AI UX Audit: What AI Catches and What It Misses',
    metaDescription: 'An honest look at an AI-powered UX audit: the systematic issues it reliably catches, the intent and qualitative why it misses, and where AI plus a human wins.',
    excerpt: 'What AI reliably catches in a product review, what it misses, and where a human still has to look.',
    category: 'Tools & Reviews',
    date: '2026-08-12',
    dateModified: '2026-08-12',
    keyword: 'ai-powered ux audit',
    cover: '/blog/ai-ux-audit-what-ai-catches.png',
  },
  {
    slug: 'financial-ux-audit',
    title: 'Financial and Fintech UX Audit: What to Check',
    metaDescription: 'Auditing a fintech product? Check trust and security signals, fee clarity, money-flow errors, KYC friction, and compliance copy. A financial UX audit guide.',
    excerpt: 'Trust signals, fee clarity, money-flow errors, KYC friction and compliance copy in fintech products.',
    category: 'Guides',
    date: '2026-08-03',
    dateModified: '2026-08-03',
    keyword: 'financial ux audit',
    cover: '/blog/financial-ux-audit.png',
  },
  {
    slug: 'ux-writing-audit',
    title: 'UX Writing Audit: How to Audit the Words in Your Product',
    seoTitle: 'UX Writing Audit: Step-by-Step Guide + Free Rubric',
    metaDescription: 'Audit every button, error message and empty state in your product. A step-by-step UX writing audit with a free rubric to score each string.',
    excerpt: 'Buttons, error messages and empty states quietly cost you users. How to review every string, with a rubric.',
    category: 'Guides',
    date: '2026-07-29',
    dateModified: '2026-07-29',
    keyword: 'ux writing audit',
    cover: '/blog/ux-writing-audit.png',
  },
  {
    slug: 'notion-ux-audit-case-study',
    title: 'Notion UX Audit: A Case Study in Power vs Simplicity (2026)',
    metaDescription: 'We ran a full UX audit on Notion across seven dimensions. It scored 54/100. Here is where a powerful product loses new users, and what your team can learn.',
    excerpt: 'We audited Notion across seven dimensions and it scored 54/100. Where a powerful product loses new users.',
    category: 'Case Studies',
    date: '2026-07-27',
    keyword: 'notion ux audit',
    cover: '/blog/notion-ux-audit-case-study.png',
  },
  {
    slug: 'cognitive-load-ux-design',
    title: 'Cognitive Load in UX Design: What Research Says (2026)',
    metaDescription: 'What cognitive load theory, Hick\'s Law, and Miller\'s Law actually tell you about UX design, and how to spot and cut mental effort in your own product.',
    excerpt: 'What cognitive load theory, Hick\'s Law and Miller\'s Law actually tell you, and how to cut mental effort in your product.',
    category: 'Research',
    date: '2026-07-20',
    keyword: 'cognitive load ux design',
    cover: '/blog/cognitive-load-ux-design.png',
  },
  {
    slug: 'heuristic-evaluation-vs-usability-testing',
    title: 'Heuristic Evaluation vs Usability Testing (2026)',
    metaDescription: 'Heuristic evaluation vs usability testing: what each method finds, what each misses, which to run first, and how to combine both without wasting budget.',
    excerpt: 'What each method finds, what each misses, and which to run first.',
    category: 'Comparisons',
    date: '2026-07-16',
    keyword: 'heuristic evaluation vs usability testing',
    cover: '/blog/heuristic-evaluation-vs-usability-testing.png',
  },
  {
    slug: 'keyboard-accessibility-testing',
    title: 'Keyboard Accessibility Testing: A 20-Minute Guide (2026)',
    metaDescription: 'Learn to test keyboard accessibility yourself in about 20 minutes. What to check, how focus should behave, common failures, and fixes for product teams.',
    excerpt: 'How to test keyboard access yourself: what to check, how focus should move, and the common failures.',
    category: 'Accessibility',
    date: '2026-07-03',
    keyword: 'keyboard accessibility testing',
    cover: '/blog/keyboard-accessibility-testing.png',
  },
  {
    slug: 'how-to-do-a-ux-audit',
    title: 'How to Do a UX Audit — Complete Step-by-Step Guide (2026)',
    seoTitle: 'How to Do a UX Audit: Complete Step-by-Step Guide (2026)',
    // "Used by 500+ product teams" removed 2026-08-17 (production has 17 users).
    // This excerpt is indexed, so the false claim was reaching search results.
    metaDescription: 'Learn how to run a professional UX audit from scratch: scope, methodology, heuristic evaluation, scoring, and presenting findings.',
    excerpt: 'Scope, method, scoring and presenting findings, step by step.',
    category: 'Guide',
    date: '2026-01-10',
    dateModified: '2026-07-27',
    keyword: 'how to do a ux audit',
  },
  {
    slug: 'ux-audit-checklist',
    title: 'UX Audit Checklist — 60-Point Framework for 2026',
    seoTitle: 'UX Audit Checklist: 60-Point Framework for 2026',
    metaDescription: 'A free UX audit checklist with 60 checkpoints across navigation, visual hierarchy, friction, accessibility and conversion design.',
    excerpt: 'A free checklist with every check explained, section by section, and how to score it.',
    category: 'Resources',
    date: '2026-01-24',
    dateModified: '2026-07-27',
    keyword: 'ux audit checklist',
  },
  {
    slug: 'best-ux-audit-tools',
    title: 'Best UX Audit Tools in 2026: Compared and Ranked',
    seoTitle: 'Best UX Audit Tools in 2026: 6 Free & AI Options Tested',
    metaDescription: 'We ran a real audit of a live SaaS product through 6 UX audit tools, free and paid. Compare speed, cost and output, and see which fits your team.',
    excerpt: 'Six tools, free and paid, run through the same real audit, and what each one is best for.',
    category: 'Tools',
    date: '2026-01-15',
    dateModified: '2026-07-27',
    keyword: 'best ux audit tools',
  },
  {
    slug: 'ux-audit-template',
    title: 'Free UX Audit Template: 30-Point Scorecard',
    seoTitle: 'Free UX Audit Template: 30-Point Printable Scorecard',
    metaDescription: 'Free UX audit template: 30 checkpoints scored out of 60 for navigation, visual hierarchy, friction, accessibility and conversion. Print it or use it online.',
    excerpt: 'A free, printable scorecard: 30 checkpoints, each scored 0 to 2, with a report skeleton for your findings.',
    category: 'Resources',
    date: '2026-01-22',
    dateModified: '2026-07-27',
    keyword: 'ux audit template free',
  },
  {
    slug: 'accessibility-audit-saas-2026',
    title: 'Accessibility Audit for SaaS — What You Need to Know in 2026',
    seoTitle: 'Accessibility Audit for SaaS: What You Need to Know in 2026',
    metaDescription: 'WCAG compliance, accessibility audits, and what changing regulations mean for SaaS products in 2026. A practical guide for product teams.',
    excerpt: 'WCAG, accessibility reviews and what changing regulations mean for SaaS products in 2026.',
    category: 'Accessibility',
    date: '2026-03-17',
    keyword: 'accessibility audit SaaS',
  },
  {
    slug: 'mobile-ux-audit-guide',
    title: 'Mobile UX Audit: A Step-by-Step Guide for Product Teams',
    metaDescription: 'A practical guide to running a mobile UX audit: what to test, how to test it, and what scores mean for your conversion rate on mobile devices.',
    excerpt: 'What to test on small screens, how to test it, and what the scores mean for mobile conversion.',
    category: 'Guides',
    date: '2026-03-10',
    keyword: 'mobile ux audit',
  },
  {
    slug: 'how-to-write-ux-audit-report',
    title: 'How to Write a UX Audit Report That Clients Actually Read',
    metaDescription: 'Most UX audit reports are too long, too technical, or too vague to be useful. Here\'s how to write one clients will read, understand, and act on.',
    excerpt: 'Most audit reports are too long or too vague to act on. How to write one clients read and act on.',
    category: 'For Designers',
    date: '2026-03-03',
    dateModified: '2026-07-27',
    keyword: 'ux audit report',
  },
  {
    slug: 'saas-conversion-optimization',
    title: 'How to Find and Fix Conversion Leaks in Your SaaS Onboarding',
    metaDescription: 'High traffic but low signups? Here\'s how to find the exact steps in your SaaS onboarding where users drop off, and how to fix them systematically.',
    excerpt: 'High traffic but low signups? How to find the step where new users drop off, and fix it.',
    category: 'Growth',
    date: '2026-02-10',
    keyword: 'SaaS conversion optimization',
  },
  {
    slug: 'ux-audit-vs-lighthouse',
    title: 'UX Audit vs Lighthouse Audit — What\'s the Difference?',
    seoTitle: 'UX Audit vs Lighthouse Audit: What\'s the Difference?',
    metaDescription: 'Lighthouse measures technical performance. A UX audit measures the human experience. Here\'s exactly what each covers and when to use which.',
    excerpt: 'Lighthouse measures technical performance, and a UX review measures the human experience. Here is what each one covers.',
    category: 'Explainers',
    date: '2026-02-24',
    keyword: 'ux audit vs lighthouse',
  },
  {
    slug: 'uxauditpro-vs-hotjar-vs-usertesting',
    title: 'UXAuditPro vs Hotjar vs UserTesting — Which One Should You Use?',
    seoTitle: 'UXAuditPro vs Hotjar vs UserTesting (2026)',
    metaDescription: 'UXAuditPro, Hotjar and UserTesting do different UX research jobs. Here\'s which one fits your situation, with a pricing reality check.',
    excerpt: 'Three tools, three different jobs, and which one fits your situation, with a pricing reality check.',
    category: 'Comparisons',
    date: '2026-03-24',
    dateModified: '2026-07-27',
    keyword: 'ux audit tool comparison',
  },
  {
    slug: 'saas-landing-page-ux-audit-findings',
    title: 'We Audited 10 SaaS Landing Pages. Here\'s What Most Get Wrong.',
    seoTitle: 'We Audited 10 SaaS Landing Pages: What Most Get Wrong',
    metaDescription: 'Real AI UX audit scores from 10 SaaS landing pages. Accessibility, conversion, mobile UX: here\'s what the data says most products consistently get wrong.',
    excerpt: 'Scores from 10 SaaS landing pages, and what most of them get wrong.',
    category: 'Research',
    date: '2026-02-17',
    keyword: 'SaaS landing page UX',
  },
  {
    slug: 'ux-audit-case-study-instagram',
    title: 'UX Audit Case Study: We Audited Instagram and Here\'s What We Found',
    seoTitle: 'UX Audit Case Study: We Audited Instagram',
    metaDescription: 'We ran a full UX audit on Instagram with dual-AI analysis across 5 dimensions. It scored 52/100. What worked, what didn\'t, and what to learn.',
    excerpt: 'We audited Instagram across 5 dimensions and it scored 52/100. What worked, what didn\'t, and what to learn.',
    category: 'Case Studies',
    date: '2026-06-03',
    keyword: 'ux audit case study',
  },
  {
    slug: 'what-does-a-user-experience-audit-include',
    title: 'What Does a User Experience Audit Actually Include?',
    metaDescription: 'What does a UX audit cover, and do you need one? What a user experience audit includes, how long it takes, and what you get at the end.',
    excerpt: 'What a user experience audit covers, how long it takes, and what you get at the end.',
    category: 'Education',
    date: '2026-06-03',
    keyword: 'user experience audit',
  },
];

const ALL_CATEGORIES = ['All', ...Array.from(new Set(blogPosts.map((p) => p.category)))];

// Topic clusters for the hub view (shown when no search or category filter is active).
// Each post is assigned by slug so the grouping stays accurate even when the loose
// "category" labels vary. Order here is the display order.
const CLUSTERS = [
  {
    name: 'Guides',
    blurb: 'Start here. What a UX audit is, how to run one, and how the methods differ.',
    slugs: [
      'how-to-do-a-ux-audit',
      'what-does-a-user-experience-audit-include',
      'how-to-write-ux-audit-report',
      'ux-audit-for-startups',
      'ux-writing-audit',
      'financial-ux-audit',
      'ecommerce-checkout-ux-audit',
      'saas-dashboard-ux-audit',
      'heuristic-evaluation-vs-usability-testing',
      'ux-audit-vs-lighthouse',
      'cognitive-load-ux-design',
    ],
  },
  {
    name: 'Checklists and Templates',
    blurb: 'Ready-to-use checkpoints and report structures you can copy.',
    slugs: ['ux-audit-checklist', 'ux-audit-template'],
  },
  {
    name: 'Tools',
    blurb: 'How the audit tools compare on accuracy, speed, price, and output.',
    slugs: ['best-ux-audit-tools', 'uxauditpro-vs-hotjar-vs-usertesting', 'ai-ux-audit-what-ai-catches', 'automated-ux-audit-vs-agency', 'figma-plugins-for-ux-review', 'heatmaps-vs-session-recordings'],
  },
  {
    name: 'Verticals',
    blurb: 'Mobile, SaaS, and real audits of products you already know.',
    slugs: [
      'free-trial-conversion-rate',
      'mobile-ux-audit-guide',
      'saas-conversion-optimization',
      'saas-landing-page-ux-audit-findings',
      'ux-audit-case-study-instagram',
      'notion-ux-audit-case-study',
    ],
  },
  {
    name: 'Accessibility',
    blurb: 'Keyboard, screen readers, contrast, and WCAG compliance in practice.',
    slugs: ['keyboard-accessibility-testing', 'accessibility-audit-saas-2026'],
  },
];

const bySlug = Object.fromEntries(blogPosts.map((p) => [p.slug, p]));
const clusteredPosts = CLUSTERS.map((c) => ({
  ...c,
  posts: c.slugs.map((s) => bySlug[s]).filter(Boolean),
}));

// Related guides for a post: same-cluster posts first (not date-ordered), then
// filled from the rest of the library so every post gets a full set. Used by the
// "Related guides" block on each post.
export function relatedGuides(slug, n = 3) {
  const cluster = CLUSTERS.find((c) => c.slugs.includes(slug));
  const ordered = [];
  if (cluster) ordered.push(...cluster.slugs.filter((s) => s !== slug));
  for (const c of CLUSTERS) {
    if (c === cluster) continue;
    ordered.push(...c.slugs.filter((s) => s !== slug));
  }
  const seen = new Set();
  const out = [];
  for (const s of ordered) {
    if (seen.has(s)) continue;
    seen.add(s);
    const post = bySlug[s];
    if (post) out.push(post);
    if (out.length >= n) break;
  }
  return out;
}

function PostCard({ post, onCategory }) {
  return (
    <Link to={`/blog/${post.slug}/`} className="block group">
      <article
        className="uxa-feature-card transition-all group-hover:shadow-md"
        style={{ background: 'var(--surface-white)' }}
      >
        {post.cover && (
          <CoverImage
            src={post.cover}
            alt={post.title}
            className="w-full rounded-xl mb-6"
            style={{ border: '1px solid var(--border-2)' }}
          />
        )}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={(e) => { e.preventDefault(); onCategory(post.category); }}
            className="uxa-tag-button inline-flex text-sm font-bold px-3 py-1 rounded-full transition-colors hover:opacity-80"
            /* --accent-tint on a white card is 1.22:1 — a filled control with no
               visible edge. The border is the cue, the same token and the same
               treatment the selected accent tab takes. Found by the guard only
               after its tag scanner was fixed to survive an arrow function in
               the first attribute; this control had been invisible to it. */
            style={{ backgroundColor: 'var(--accent-tint)', color: 'var(--accent-text)', border: '1px solid var(--border-2)' }}
          >
            {post.category}
          </button>
          <span className="flex items-center gap-1 text-sm" style={{ color: 'var(--ink-2)' }}>
            <Clock className="w-3 h-3" /> {readingTime(postContent[post.slug])}
          </span>
          <span className="text-sm" style={{ color: 'var(--ink-2)' }}>
            {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
        <h3
          className="uxa-h3 mb-3 group-hover:text-orange-500 transition-colors"
          style={{ color: 'var(--ink-1)' }}
        >
          {post.title}
        </h3>
        <p className="uxa-dense mb-4" style={{ color: 'var(--ink-2)' }}>
          {post.excerpt}
        </p>
        <span className="inline-flex items-center gap-1 text-sm font-bold" style={{ color: 'var(--accent-text)' }}>
          Read article <ArrowRight className="w-4 h-4" />
        </span>
      </article>
    </Link>
  );
}

export default function BlogIndexPage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = blogPosts.filter((post) => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const q = search.toLowerCase();
    const matchesSearch = !q || post.title.toLowerCase().includes(q) || post.excerpt.toLowerCase().includes(q) || post.category.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  // Default hub view: no search text and no category filter selected.
  const showClusters = activeCategory === 'All' && !search.trim();

  return (
    <div style={{ minHeight: '100vh' }}>
      <SEO
        fullTitle="UXAuditPro Blog"
        description="Field notes, case studies and how-tos from the UXAuditPro team on making digital products easier to use."
        canonical="/blog"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'UXAuditPro Blog',
          url: 'https://www.uxauditpro.com/blog/',
          description: 'Expert guides, templates, and tool reviews for UX professionals and product teams.',
          publisher: {
            '@type': 'Organization',
            name: 'UXAuditPro',
            logo: { '@type': 'ImageObject', url: 'https://www.uxauditpro.com/logo.png' },
          },
        }}
      />

      {/* Hero */}
      <header className="uxa-pt-xl uxa-pb-md px-6 hero" style={{ position: 'relative', textAlign: 'center' }}>
        <div className="uxa-hero-grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto' }}>
          <span className="section-label inline-block mb-6">Blog</span>
          <h1
            className="uxa-h1 mb-6"
            style={{ color: 'var(--ink-1)' }}
          >
            The UXAuditPro{' '}
            <span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>blog</span>
          </h1>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', maxWidth: 620, margin: '20px auto 12px' }}>
            Practical guides, free templates, and tool comparisons for product teams who take UX seriously. New article every week.
          </p>
          {/* The one pointer to the pillar (owner, 2026-10-07): /blog/ was ranking
              for "ux audit" above the page that should own it, so the hub sends
              that query to /ux-audit/ rather than competing for it. */}
          <p style={{ color: 'var(--ink-2)', margin: '0 auto 40px', maxWidth: 620 }}>
            New to UX audits? Start with{' '}
            <Link to="/ux-audit/" style={{ color: 'var(--accent-text)', textDecoration: 'underline' }}>what a UX audit is</Link>.
          </p>

          {/* Search */}
          <div className="relative max-w-lg mx-auto mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-2)' }} />
            <input
              type="search"
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-10 py-3 rounded-full text-sm outline-none"
              style={{ backgroundColor: 'var(--surface-white)', border: '1.5px solid var(--border-2)', color: 'var(--ink-1)' }}
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4" style={{ color: 'var(--ink-2)' }} />
              </button>
            )}
          </div>

          {/* Category filters. PHASE F: PillTabs, tone ACCENT — a filter over
              content, which is what the accent tone is drawn for. This was a
              hand-rolled row of buttons with no role, no keyboard handling and
              no accessible name for the group. A set of mutually exclusive
              filters IS a tablist, and it says so now: arrow keys move between
              categories, roving tabindex means one stop for the whole row, and
              the 26px pill carries a 44px target. The tint carries the
              selection and --accent-text carries the label; the accent itself
              is never the text colour. */}
          <div className="flex flex-wrap justify-center">
            <PillTabs
              items={ALL_CATEGORIES}
              value={Math.max(0, ALL_CATEGORIES.indexOf(activeCategory))}
              onChange={(i) => setActiveCategory(ALL_CATEGORIES[i])}
              tone="accent"
              size="md"
              ariaLabel="Filter posts by category"
              className="flex-wrap justify-center"
            />
          </div>
        </div>
      </header>

      {/* Intro: routes "what is a UX audit" readers to the pillar page. It used to
          answer the question here too, which made /blog/ compete with /ux-audit/
          for "ux audit"; that copy went on 2026-10-07 (owner, SEO Task 3).
          Only shown in the default hub view. */}
      {showClusters && (
        <section className="uxa-pb-xs px-6">
          <div className="max-w-3xl mx-auto">
            {/* #3C3C48 was a stray off-palette grey. --ink-2 is the token for
                secondary prose and measures 6.84:1 on the canvas. */}
            {/* Was --article-size (19px) on a UI page: the right value on the
                wrong surface. /blog/ is a hub, not an article - no .uxa-article
                anywhere on it - so its intro prose takes the UI lead. */}
            <div style={{ fontSize: 'var(--type-lead-size)', lineHeight: 'var(--type-lead-leading)', color: 'var(--ink-2)' }}>
              <p className="mb-6">
                This blog is our working library. The guides below are grouped by what you are trying to do, whether that is learning the method, grabbing a checklist, choosing a tool, or reading a real audit end to end. If you want the full picture in one place first, start with the complete guide.
              </p>
            </div>

            {/* Prominent pillar link */}
            <Link to="/ux-audit/" className="block group">
              <div
                className="uxa-feature-card flex items-center justify-between gap-4 transition-all group-hover:shadow-md"
                /* #FFD3C2 was a hand-mixed border one step off --accent-soft.
                   The card ground stays --accent-tint, which is what marks this
                   as the page's one promoted link. */
                style={{ background: 'var(--accent-tint)', borderColor: 'var(--accent-soft)', padding: 'var(--space-6)', height: 'auto' }}
              >
                <div>
                  <p className="text-sm font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--accent-text)' }}>Start here</p>
                  <p className="font-bold" style={{ color: 'var(--ink-1)', fontSize: '18px', letterSpacing: '-0.01em' }}>
                    What is a UX audit? The complete guide
                  </p>
                  <p className="uxa-dense mt-1" style={{ color: 'var(--ink-2)' }}>
                    What it covers, when to run one, the method, tools, and templates, in one place.
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-bold whitespace-nowrap" style={{ color: 'var(--accent-text)' }}>
                  Read the guide <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Posts */}
      <section className="uxa-pb-xl px-6 uxa-pt-sm">
        <div className="max-w-4xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <p style={{ color: 'var(--ink-2)', fontSize: '16px' }}>
                No articles match "{search}". Try a different search or{' '}
                <button onClick={() => { setSearch(''); setActiveCategory('All'); }} style={{ color: 'var(--accent-text)', fontWeight: 700 }}>clear filters</button>.
              </p>
            </div>
          ) : showClusters ? (
            <div className="space-y-16">
              {clusteredPosts.map((cluster) => (
                <div key={cluster.name}>
                  <div className="mb-6">
                    <h2 className="uxa-h2" style={{ color: 'var(--ink-1)' }}>
                      {cluster.name}
                    </h2>
                    <p className="uxa-dense mt-1" style={{ color: 'var(--ink-2)' }}>{cluster.blurb}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {cluster.posts.map((post) => (
                      <PostCard key={post.slug} post={post} onCategory={setActiveCategory} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <p className="text-sm mb-6" style={{ color: 'var(--ink-2)' }}>
                {filtered.length} article{filtered.length !== 1 ? 's' : ''}{activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
              </p>
              <div className="grid grid-cols-1 gap-6">
                {filtered.map((post) => (
                  <PostCard key={post.slug} post={post} onCategory={setActiveCategory} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>
            Ready to run your first audit?
          </h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 520 }}>
            Stop reading about UX audits and run one in under 5 minutes. One free audit every month.
          </p>
          <div style={{ position: 'relative', display: 'inline-block', marginTop: 40, isolation: 'isolate' }}>
            <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
              <Link to="/ux-auditor/" className="uxa-submit uxa-submit--auto">
                Try UXAuditPro Free
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
